/**
 * External Imports
 */
import { FC, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuIcon, NeuButton } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import EmployeeSearch from '../reusableModalPieces/employeeSearch';

import { RootState } from '../../../redux/store';

import { handleModal } from '../../../services/ModalPortal/utils';

import { logRoundProfile } from '../../../utils/analyticsHelpers';

/**
 * Global Type Definition Imports
 */
import { Employee } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CardHeader,
  ModalCardContent,
  ModalFooter,
  ModalTitle,
  UnlistedPatientRoundCard
} from './UnlistedEmployeeRoundModal.styles';

const UnlistedEmployeeRoundModal: FC = () => {
  const [employeeObj, setEmployeeObj] = useState<Employee>({} as Employee);
  const [showButton, setShowButton] = useState<boolean>(false);
  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  const navigate = useNavigate();

  const { logTrackingEvent } = useAnalyticsApi();
  const { pathname } = useLocation();
  const page = pathname.split('/')[1];

  const selectEmployee = (emp: Employee) => {
    setEmployeeObj(emp);
    setShowButton(true);
  };

  const handlePost = () => {
    if (showButton) {
      logTrackingEvent(logRoundProfile());
      navigate(
        page === 'employees'
          ? '/employees/profile'
          : page === 'validation'
          ? pathname.includes('nlr/list')
            ? '/validation/profile/nlr'
            : pathname.includes('bssr/list')
            ? '/validation/profile/bssr'
            : pathname.includes('aidet/list')
            ? '/validation/profile/aidet'
            : '/validation/profile/safety'
          : page === 'support'
          ? pathname.includes('csc/list')
            ? '/support/profile/csc'
            : '/support/profile/cned'
          : '/',
        {
          state: {
            from: pathname,
            employeeId: employeeObj.employeeId
          }
        }
      );
      handleModal(modalContentRef);
    }
  };

  const handleRemoveChip = () => {
    setEmployeeObj({} as Employee);
    setShowButton(false);
  };

  return (
    <UnlistedPatientRoundCard
      id="Unlisted-Employee-Modal"
      className="show-modal"
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <ModalTitle>
          <NeuIcon size="3x" color="blue" style={{ alignItems: 'center' }}>
            supervisor_account
          </NeuIcon>
          Search All Employees
        </ModalTitle>
        <NeuButton
          aria-label="close panel"
          data-testid="unlisted-employee-close-button"
          kind="tertiary"
          color="blue"
          icon="close"
          size="small"
          onClick={() => handleModal(modalContentRef)}
        >
          Close
        </NeuButton>
      </CardHeader>
      <ModalCardContent className="pt-2" unit="content">
        <EmployeeSearch
          chipVal={
            employeeObj && Object.keys(employeeObj).length > 0
              ? `${employeeObj?.firstName} ${employeeObj?.lastName}`
              : undefined
          }
          showLabel={false}
          val={employeeObj.employeeId ?? ''}
          handleRemoveEmployee={handleRemoveChip}
          setEmployeeSelection={(emp: Employee) => selectEmployee(emp)}
        />
      </ModalCardContent>
      <ModalFooter unit="footer">
        <NeuButton
          color="blue"
          data-testid="unlisted-employee-start-round-button"
          disabled={!showButton ? true : undefined}
          size="small"
          style={{
            marginRight: 20,
            marginBottom: 26,
            marginLeft: page === 'validation' ? 232 : 263
          }}
          onClick={handlePost}
        >
          Start {page === 'validation' ? 'Validation' : 'Round'}
        </NeuButton>
      </ModalFooter>
    </UnlistedPatientRoundCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    modalState: state.ModalReducer.modal
  };
};

export default connect(mapReduxStateToProps)(UnlistedEmployeeRoundModal);
